import { PERMISSIONS } from '@ibe/constants';
import { Button, ConfirmDialog } from '@ibe/ui';
import { Eye, Pencil, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { PermissionGuard } from '../../../auth/permission-guard.js';
import { PrimaryCell, RowActions } from '../../../components/resource-list/cells.js';
import { ResourceListPage } from '../../../components/resource-list/resource-list-page.js';
import { type ColumnDescriptor } from '../../../lib/resource-list/columns.js';
import { exchangeRates, exchangeRateEndpoints } from '../api/endpoints.js';
import { exchangeRateRow, type ExchangeRateRow } from '../api/schemas.js';

function formatDate(value: string | undefined): string {
  if (value === undefined) return '';
  return new Date(value).toLocaleDateString('en-AU', {
    timeZone: 'UTC',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatPair(row: ExchangeRateRow): string {
  return `${row.baseCurrency ?? ''}/${row.quoteCurrency ?? ''}`;
}

const columns: readonly ColumnDescriptor<ExchangeRateRow>[] = [
  {
    id: 'pair',
    header: 'Pair',
    sortable: true,
    alwaysVisible: true,
    fields: ['baseCurrency', 'quoteCurrency'],
    cell: (row) => (
      <PrimaryCell
        label={formatPair(row)}
        to={`/exchange-rates/${row.id}`}
        secondary={row.source ?? null}
      />
    ),
  },
  {
    id: 'rate',
    header: 'Rate',
    sortable: false,
    fields: ['rate'],
    cell: (row) => row.rate,
  },
  {
    id: 'source',
    header: 'Source',
    sortable: true,
    fields: ['source'],
    cell: (row) => row.source,
  },
  {
    id: 'effectiveAt',
    header: 'Effective',
    sortable: true,
    fields: ['effectiveAt'],
    cell: (row) => formatDate(row.effectiveAt),
  },
  {
    id: 'expiresAt',
    header: 'Expires',
    sortable: true,
    fields: ['expiresAt'],
    cell: (row) => formatDate(row.expiresAt),
  },
  {
    id: 'createdAt',
    header: 'Created',
    sortable: true,
    fields: ['createdAt'],
    defaultVisible: false,
    cell: (row) => formatDate(row.createdAt),
  },
  {
    id: 'actions',
    header: '',
    headerLabel: 'Actions',
    align: 'right',
    alwaysVisible: true,
    fields: ['baseCurrency', 'quoteCurrency'],
    cell: (row: ExchangeRateRow): React.JSX.Element => <ExchangeRateRowActions row={row} />,
  },
];

function ExchangeRateRowActions({ row }: { readonly row: ExchangeRateRow }): React.JSX.Element {
  const [confirming, setConfirming] = useState(false);
  const softDelete = exchangeRates.useSoftDelete();
  const label = formatPair(row);

  return (
    <>
      <div className="flex flex-nowrap items-center justify-end gap-1">
        <RowActions
          actions={[
            { label: `View ${label}`, icon: Eye, to: `/exchange-rates/${row.id}` },
            { label: `Edit ${label}`, icon: Pencil, to: `/exchange-rates/${row.id}/edit` },
          ]}
        />
        <PermissionGuard permission={PERMISSIONS.EXCHANGE_RATE_DELETE}>
          <RowActions
            actions={[
              {
                label: `Delete ${label}`,
                icon: Trash2,
                destructive: true,
                onClick: () => {
                  setConfirming(true);
                },
              },
            ]}
          />
        </PermissionGuard>
      </div>
      <ConfirmDialog
        open={confirming}
        onOpenChange={setConfirming}
        title="Delete exchange rate"
        description={`${label} will be hidden from future conversions.`}
        confirmLabel="Delete"
        destructive
        loading={softDelete.isPending}
        onConfirm={() => {
          softDelete.mutate(row.id, {
            onSuccess: () => {
              setConfirming(false);
            },
          });
        }}
      />
    </>
  );
}

export function ExchangeRateListPage(): React.JSX.Element {
  return (
    <ResourceListPage
      title="Exchange rates"
      description="The rates used to convert between currencies."
      path={exchangeRateEndpoints.list}
      resource="exchangeRates"
      schema={exchangeRateRow}
      columns={columns}
      rowKey={(row) => row.id}
      permission={PERMISSIONS.EXCHANGE_RATE_READ}
      actions={
        <PermissionGuard permission={PERMISSIONS.EXCHANGE_RATE_CREATE}>
          <Button asChild>
            <Link to="/exchange-rates/new">
              <Plus className="h-4 w-4" aria-hidden="true" />
              New exchange rate
            </Link>
          </Button>
        </PermissionGuard>
      }
    />
  );
}
